import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { FragmentOf } from '@eluve/graphql.tada';

import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from '../../../template-bundles/template-bundle.operations';

import { AffectedUsersPreview } from './AffectedUsersPreview';
import { BulkResultSummary } from './BulkResultSummary';
import { getTenantUsersForBulkBundleQuery } from './bulk-default-bundle.operations';
import {
  type BulkTargetUser,
  useBulkUpdateDefaultBundle,
} from './useBulkUpdateDefaultBundle';

type TemplateBundleListItem = FragmentOf<typeof templateBundleListItemFragment>;

interface UpdateBundleBySpecialtyDialogProps {
  tenantId: string;
  tenantName: string;
  bundles: TemplateBundleListItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NO_SELECTION = '';

type SpecialtyOption = {
  /** Lowercased value used as a stable, case-insensitive key. */
  key: string;
  /** First-seen original casing — shown verbatim in the UI. */
  display: string;
};

/**
 * Builds the unique-specialty option list for the current tenant.
 * `users.specialty` is citext + free-form, so values may differ only in
 * case/whitespace. We dedupe case-insensitively on a trimmed, lowercased
 * key but render the first encountered original spelling so the operator
 * sees the data as it actually exists.
 */
const buildSpecialtyOptions = (
  specialties: Array<string | null | undefined>,
): SpecialtyOption[] => {
  const map = new Map<string, SpecialtyOption>();
  for (const raw of specialties) {
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (!map.has(key)) map.set(key, { key, display: trimmed });
  }
  return [...map.values()].sort((a, b) => a.display.localeCompare(b.display));
};

const matchesSpecialty = (
  candidate: string | null | undefined,
  selectedKey: string,
): boolean => {
  if (!candidate) return false;
  return candidate.trim().toLowerCase() === selectedKey;
};

export const UpdateBundleBySpecialtyDialog: React.FC<
  UpdateBundleBySpecialtyDialogProps
> = ({ tenantId, tenantName, bundles, open, onOpenChange }) => {
  const toast = useToast();

  const [specialtyKey, setSpecialtyKey] = useState<string>(NO_SELECTION);
  const [targetBundleId, setTargetBundleId] = useState<string>(NO_SELECTION);

  const { data, loading: loadingUsers } = useQuery(
    getTenantUsersForBulkBundleQuery,
    {
      variables: { tenantId },
      context: eluveAdminHasuraContext,
      skip: !open,
      fetchPolicy: 'cache-and-network',
    },
  );

  const tenantUsers = useMemo(
    () => data?.tenantsByPk?.tenantUsers ?? [],
    [data],
  );

  const bundlesById = useMemo(() => {
    const map = new Map<string, TemplateBundleListItem>();
    bundles.forEach((b) => map.set(b.id, b));
    return map;
  }, [bundles]);

  const specialtyOptions = useMemo(
    () => buildSpecialtyOptions(tenantUsers.map((tu) => tu.user?.specialty)),
    [tenantUsers],
  );

  const selectedSpecialty = useMemo(
    () => specialtyOptions.find((s) => s.key === specialtyKey) ?? null,
    [specialtyOptions, specialtyKey],
  );

  const affectedUsers = useMemo(() => {
    if (!specialtyKey) return [];
    return tenantUsers.filter((tu) =>
      matchesSpecialty(tu.user?.specialty, specialtyKey),
    );
  }, [tenantUsers, specialtyKey]);

  const previewRows = useMemo(
    () =>
      affectedUsers.map((tu) => {
        const currentBundleId = tu.settings?.defaultTemplateBundleId ?? null;
        const currentBundle = currentBundleId
          ? (bundlesById.get(currentBundleId) ?? null)
          : null;
        return {
          userId: tu.userId,
          firstName: tu.user?.firstName ?? null,
          lastName: tu.user?.lastName ?? null,
          email: tu.user?.email ?? null,
          specialty: tu.user?.specialty ?? null,
          currentBundleName: currentBundle
            ? getTemplateBundleDisplayName(currentBundle)
            : null,
        };
      }),
    [affectedUsers, bundlesById],
  );

  const { status, results, run, reset } = useBulkUpdateDefaultBundle(tenantId);

  useEffect(() => {
    if (!open) {
      setSpecialtyKey(NO_SELECTION);
      setTargetBundleId(NO_SELECTION);
      reset();
    }
  }, [open, reset]);

  const isRunning = status === 'running';
  const isDone = status === 'done';

  const canSubmit =
    !isRunning &&
    !isDone &&
    specialtyKey !== NO_SELECTION &&
    targetBundleId !== NO_SELECTION &&
    previewRows.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const targets: BulkTargetUser[] = affectedUsers.map((tu) => ({
      userId: tu.userId,
      firstName: tu.user?.firstName ?? null,
      lastName: tu.user?.lastName ?? null,
      email: tu.user?.email ?? null,
    }));
    const settled = await run(targets, targetBundleId);
    const failed = settled.filter((r) => !r.ok).length;
    if (failed === 0) {
      toast.success(`Updated default bundle for ${settled.length} user(s)`);
    } else {
      toast.error(
        `Updated ${settled.length - failed}/${settled.length}; ${failed} failed`,
      );
    }
  };

  // Block Escape/X dismissal mid-run: closing while in flight would reset the
  // hook's status/results to idle, but the per-row mutations would still settle
  // and write back to the (now stale) state. A subsequent run would race over
  // the same state and silently overwrite the first run's results.
  const handleOpenChange = (next: boolean) => {
    if (!next && isRunning) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Update Default Bundle by Specialty</DialogTitle>
          <DialogDescription>
            Tenant: <strong>{tenantName}</strong>
          </DialogDescription>
        </DialogHeader>

        <Stack gap={4} align="stretch">
          <Stack gap={1}>
            <Label>Specialty</Label>
            <Combobox disabled={loadingUsers || isRunning || isDone}>
              <ComboboxSelectButton className="w-full">
                {selectedSpecialty
                  ? selectedSpecialty.display
                  : loadingUsers
                    ? 'Loading specialties...'
                    : specialtyOptions.length === 0
                      ? 'No specialties set on any user'
                      : 'Select specialty'}
              </ComboboxSelectButton>
              <ComboboxDropdown searchPlaceholder="Search specialties">
                {specialtyOptions.map((opt) => (
                  <ComboboxOption
                    key={opt.key}
                    searchValue={opt.display}
                    onSelect={() => setSpecialtyKey(opt.key)}
                  >
                    <Stack gap={2} direction="horizontal" justify="between">
                      <Text inline>{opt.display}</Text>
                      {specialtyKey === opt.key && (
                        <Icon name="Check" size="xl" />
                      )}
                    </Stack>
                  </ComboboxOption>
                ))}
              </ComboboxDropdown>
            </Combobox>
            <Text size="xs" color="supporting">
              Users with no specialty set are excluded.
            </Text>
          </Stack>

          {specialtyKey !== NO_SELECTION && (
            <AffectedUsersPreview
              users={previewRows}
              showCurrentBundle
              emptyMessage={
                loadingUsers
                  ? 'Loading users...'
                  : 'No users in this tenant match that specialty.'
              }
            />
          )}

          <Stack gap={1}>
            <Label htmlFor="specialty-target-bundle">Target bundle</Label>
            <Select
              value={targetBundleId || undefined}
              onValueChange={setTargetBundleId}
              disabled={
                specialtyKey === NO_SELECTION ||
                previewRows.length === 0 ||
                isRunning ||
                isDone
              }
            >
              <SelectTrigger id="specialty-target-bundle" className="w-full">
                <SelectValue placeholder="Select target bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {getTemplateBundleDisplayName(b)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>

          {isDone && <BulkResultSummary results={results} />}
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isRunning}
          >
            {isDone ? 'Close' : 'Cancel'}
          </Button>
          {!isDone && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={isRunning}
            >
              {isRunning
                ? 'Updating...'
                : `Confirm update of ${previewRows.length} user${
                    previewRows.length === 1 ? '' : 's'
                  }`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
